'use client';

import { storyblokEditable } from '@storyblok/react';
import { useMemo, useState } from 'react';

import { Image } from '@/components/image';
import { Pill } from '@/components/pill';

import { isImage } from '@/helpers/image';
import { render } from '@/helpers/storyblok/rich-text';
import { markdownToHtml } from '@/helpers/string';

const pickImage = (item, fallback) => {
  return isImage(item?.image) ? item.image : fallback;
};

export const AdvantagesClient = ({
  blok,
  items,
  parentImage,
  blocksSlot,
}) => {
  const TitleEl = blok.titleLevel || 'h2';

  const fallbackImage = useMemo(() => {
    const firstItemImage = items.find((item) => {
      return isImage(item.image);
    })?.image;

    return (
      (isImage(firstItemImage) ? firstItemImage : null) ||
      (isImage(parentImage) ? parentImage : null)
    );
  }, [items, parentImage]);

  const [activeUid, setActiveUid] = useState(null);

  const activeItem = items.find((item) => {
    return item._uid === activeUid;
  });
  const activeImage = pickImage(activeItem, fallbackImage);

  return (
    <div className="advantages__grid">
      <div className="advantages__content">
        {blok.pill || blok.title ? (
          <div className="advantages__heading">
            {blok.pill ? <Pill>{blok.pill}</Pill> : null}

            {blok.title ? (
              <TitleEl
                className="advantages__title"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(blok.title),
                }}
              />
            ) : null}
          </div>
        ) : null}

        {items.length > 0 ? (
          <ul className="advantages__items">
            {items.map((item) => {
              const ItemTitleEl = item.titleLevel || 'h3';
              const isActive = item._uid === activeUid;

              return (
                <li
                  key={item._uid}
                  {...storyblokEditable(item)}
                  className={
                    isActive
                      ? 'advantages__item advantages__item--active'
                      : 'advantages__item'
                  }
                  onMouseEnter={() => {
                    return setActiveUid(item._uid);
                  }}
                  onMouseLeave={() => {
                    return setActiveUid((current) => {
                      return current === item._uid ? null : current;
                    });
                  }}
                  onFocus={() => {
                    return setActiveUid(item._uid);
                  }}
                  onBlur={() => {
                    return setActiveUid((current) => {
                      return current === item._uid ? null : current;
                    });
                  }}
                  onClick={() => {
                    return setActiveUid(item._uid);
                  }}
                  tabIndex={0}
                >
                  {item.number ? (
                    <span
                      className="advantages__item__number"
                      aria-hidden="true"
                    >
                      {item.number}
                    </span>
                  ) : null}

                  <div className="advantages__item__body">
                    {item.title ? (
                      <ItemTitleEl
                        className="advantages__item__title"
                        dangerouslySetInnerHTML={{
                          __html: markdownToHtml(item.title),
                        }}
                      />
                    ) : null}

                    {item.content ? (
                      <div className="advantages__item__content">
                        {render(item.content)}
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <div className="advantages__aside">
        {isImage(activeImage) ? (
          <div className="advantages__image">
            <Image
              key={activeImage.filename}
              image={activeImage}
              sizes="(min-width: 1024px) 640px, 100vw"
            />
          </div>
        ) : null}

        {blocksSlot}
      </div>
    </div>
  );
};
